import React from 'react';
import Modal from './ui/Modal';

const VersionChangeModal = ({ isOpen, onClose, changes }) => {
    if (!isOpen || !changes || changes.length === 0) return null;

    const getChangeTypeColor = (type) => {
        switch (type) {
            case 'feature':
                return 'bg-green-100 text-green-800';
            case 'fix':
                return 'bg-blue-100 text-blue-800';
            case 'improvement':
                return 'bg-yellow-100 text-yellow-800';
            case 'breaking':
                return 'bg-red-100 text-red-800';
            case 'admin':
                return 'bg-teal-100 text-teal-800';
            case 'teachers':
                return 'bg-orange-100 text-orange-800';
            case 'all':
                return 'bg-indigo-100 text-indigo-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getChangeTypeIcon = (type) => {
        switch (type) {
            case 'feature':
                return '✨';
            case 'fix':
                return '🐛';
            case 'improvement':
                return '⚡';
            case 'breaking':
                return '⚠️';
            case 'admin':
                return '🛠️';
            case 'teachers':
                return '👩‍🏫';
            case 'all':
                return '🧭';
            default:
                return '📝';
        }
    };

    const getChangeTypeLabel = (type) => {
        switch (type) {
            case 'feature':
                return 'Features';
            case 'fix':
                return 'Fixes';
            case 'improvement':
                return 'Improvements';
            case 'breaking':
                return 'Breaking Changes';
            case 'admin':
                return 'Admin';
            case 'teachers':
                return 'Teachers';
            case 'all':
                return 'General';
            default:
                return 'Other';
        }
    };

    const typeOrder = ['breaking', 'feature', 'improvement', 'fix', 'admin', 'teachers', 'all', 'other'];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Your Pi-Schedule has been updated to ${changes && changes.length > 0 ? changes[0].version : ''}!`}
        >
            <div className="space-y-6">
                <div className="text-gray-600 mb-4">
                    Here are the latest updates and improvements to Pi-Schedule:
                </div>

                {changes.map((version, versionIndex) => (
                    <div key={version.version} className="border-b pb-4 last:border-b-0">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Version {version.version}
                                {versionIndex === 0 && (
                                    <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                                        Current
                                    </span>
                                )}
                            </h3>
                            <span className="text-sm text-gray-500">
                                {new Date(version.releaseDate).toLocaleDateString()}
                            </span>
                        </div>

                        {(() => {
                            const groupedChanges = version.changes.reduce((acc, change) => {
                                const key = change.type || 'other';
                                if (!acc[key]) {
                                    acc[key] = [];
                                }
                                acc[key].push(change);
                                return acc;
                            }, {});

                            const sortedTypes = Object.keys(groupedChanges).sort((a, b) => {
                                const aIndex = typeOrder.indexOf(a) === -1 ? typeOrder.indexOf('other') : typeOrder.indexOf(a);
                                const bIndex = typeOrder.indexOf(b) === -1 ? typeOrder.indexOf('other') : typeOrder.indexOf(b);
                                return aIndex - bIndex;
                            });

                            return sortedTypes.map((type) => (
                                <div key={type} className="mb-4 last:mb-0">
                                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getChangeTypeColor(type)}`}>
                                        <span>{getChangeTypeIcon(type)}</span>
                                        <span>{getChangeTypeLabel(type)}</span>
                                    </div>
                                    <ul className="mt-2 list-disc pl-6 space-y-2">
                                        {groupedChanges[type].map((change, changeIndex) => (
                                            <li key={changeIndex} className="text-gray-700">
                                                <span className={change.importance === 'high' ? 'font-bold' : ''}>
                                                    {change.description}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ));
                        })()}
                    </div>
                ))}

                <div className="flex justify-end pt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Got it!
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default VersionChangeModal;